import { LOGOUT } from 'constants/names.js';
import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTeamColors } from 'hooks';

import types from 'redux/types';
import { clearLocalStorage } from 'helpers';

import Button from 'components/Button';
import { GoogleLogoutButton } from 'components/AuthContainer/LoginForm/GoogleLogoutButton';

import {
  SlidePanelContainer,
  CloseButton,
  Backdrop,
  FooterNotification,
  LogoutContainer,
} from './style.js';

const UserProfile = ({ isOpen, onClose }) => {
  const { email } = useSelector((state) => state.user.data);
  const userId = useSelector((state) => state.user.data.user_id);
  const isUserAuth = !!useSelector((state) => state.user.data.token);
  const isGoogleId = !!useSelector((state) => state.user.data.googleId);
  const configData = useSelector((state) => state.configuration?.data);
  const [hasNotificationToken, setHasNotificationToken] = React.useState(false);

  // Handle both string '1' and integer 1 from API
  const isPushEnabled =
    configData?.push_notifications_enabled === 1 ||
    configData?.push_notifications_enabled === '1';
  const userMenuRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mainColor } = useTeamColors();

  // Check if user has notification permission and token
  const checkNotificationStatus = React.useCallback(() => {
    if (userId && isPushEnabled) {
      // Check if browser has granted notification permission
      // and if there's a valid service worker registration with push subscription
      if ('Notification' in window && Notification.permission === 'granted') {
        navigator.serviceWorker.ready
          .then((registration) => {
            registration.pushManager.getSubscription().then((subscription) => {
              setHasNotificationToken(!!subscription);
            });
          })
          .catch(() => {
            setHasNotificationToken(false);
          });
      } else {
        setHasNotificationToken(false);
      }
    }
  }, [userId, isPushEnabled]);

  React.useEffect(() => {
    checkNotificationStatus();
  }, [checkNotificationStatus]);

  // Listen for push permission granted event to update status immediately
  React.useEffect(() => {
    const handlePushPermissionGranted = () => {
      // Wait a bit for the subscription to be created
      setTimeout(() => {
        checkNotificationStatus();
      }, 1000);
    };

    window.addEventListener(
      'pushPermissionGranted',
      handlePushPermissionGranted
    );
    return () =>
      window.removeEventListener(
        'pushPermissionGranted',
        handlePushPermissionGranted
      );
  }, [checkNotificationStatus]);

  const handleClickEsc = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleClickOutside = useCallback(
    (e) => {
      if (
        isOpen &&
        userMenuRef.current &&
        !userMenuRef.current?.contains(e.target)
      ) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  const handleLogout = useCallback(() => {
    dispatch({
      type: types.USER_LOGIN_RESET,
    });

    dispatch({
      type: types.SET_SELECTED_TEAM,
      payload: { teamName: '', teamId: '' },
    });

    dispatch({
      type: types.SET_SELECTED_LEAGUE,
      payload: { leagueName: '', leagueId: '' },
    });

    clearLocalStorage();
    onClose();
    navigate('/');
  }, [dispatch, navigate, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Re-check notification status when panel opens
      checkNotificationStatus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, checkNotificationStatus, handleClickOutside]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleClickEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleClickEsc);
    };
  });

  return (
    <>
      <Backdrop $isOpen={isOpen} />
      <SlidePanelContainer $isOpen={isOpen} ref={userMenuRef}>
        <CloseButton onClick={onClose} />
        {email ? (
          <FooterNotification $color={mainColor}>
            Signed in as {email}
          </FooterNotification>
        ) : null}

        {isPushEnabled && hasNotificationToken ? (
          <FooterNotification $color={mainColor}>
            * User is registered to receive web push notifications for this
            site.
          </FooterNotification>
        ) : null}

        {isUserAuth ? (
          <LogoutContainer>
            {isGoogleId ? (
              <GoogleLogoutButton />
            ) : (
              <Button onClick={handleLogout} title={LOGOUT} />
            )}
          </LogoutContainer>
        ) : null}
      </SlidePanelContainer>
    </>
  );
};

export default UserProfile;
